import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, BellRing, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Reusable "Notify me" waitlist capture modal.
 * Posts to /api/waitlist with the given product/category identifier.
 */
export const NotifyMeDialog = ({ open, onOpenChange, categoryTitle = '', productId = '', productName = '' }) => {
  const [email, setEmail] = useState('');
  const [joining, setJoining] = useState(false);
  const [joined, setJoined] = useState(false);

  const reset = (v) => {
    onOpenChange(v);
    if (!v) { setTimeout(() => { setEmail(''); setJoined(false); setJoining(false); }, 200); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setJoining(true);
    try {
      await axios.post(`${API}/waitlist`, {
        email: email.trim(),
        product_id: productId,
        product_name: productName || categoryTitle,
      });
      setJoined(true);
    } catch {
      toast.error('Sorry, something went wrong — please try again.');
    } finally {
      setJoining(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogContent className="bg-stone-50 border-emerald-100 max-w-md" data-testid="notify-me-dialog">
        {joined ? (
          <div className="text-center py-4" data-testid="notify-me-success">
            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-7 h-7 text-emerald-600" />
            </div>
            <h3 className="text-xl font-heading font-semibold text-emerald-950 mb-1.5">You&apos;re on the list.</h3>
            <p className="text-sm text-stone-600">We&apos;ll email you the moment{categoryTitle ? ` ${categoryTitle}` : ' this'} is available.</p>
          </div>
        ) : (
          <>
            <DialogHeader>
              <div className="w-11 h-11 rounded-xl bg-amber-100 flex items-center justify-center mb-2">
                <BellRing className="w-5 h-5 text-amber-600" />
              </div>
              <DialogTitle className="font-heading text-2xl text-emerald-950">
                {categoryTitle ? `${categoryTitle} — coming soon` : 'Coming soon'}
              </DialogTitle>
              <DialogDescription className="text-stone-600">
                Be the first to know when this formulation is available. No spam, just the launch.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="mt-2 flex flex-col gap-3" data-testid="notify-me-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="w-full border-2 border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-emerald-500 bg-white"
                data-testid="notify-me-email"
              />
              <button
                type="submit"
                disabled={joining}
                className="w-full inline-flex items-center justify-center gap-2 bg-emerald-800 hover:bg-emerald-900 text-white font-semibold py-3.5 rounded-xl transition-colors disabled:opacity-60"
                data-testid="notify-me-submit"
              >
                {joining ? <Loader2 className="w-4 h-4 animate-spin" /> : <BellRing className="w-4 h-4" />}
                Notify Me
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NotifyMeDialog;
